import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from "../../stores/dashboard.store"
import { CircularProgress, IconButton, LinearProgress } from '@mui/material'
import { listMediaByBlueprintID } from "../../api-client/data"
import useFetch from "../../hooks/useFetch"
import RefreshIcon from '@mui/icons-material/Refresh'

import { DashboardBody, DashboardHeader } from '../dashboard-container'
import { formatDateTime } from '../../util/time'
import { formatBytes } from "../../util/util"
import VideoDialog from '../dialogs/video-player'
import DeleteMediaDialog from '../dialogs/delete-media'
import { deleteMediaById } from '../../api-client/data'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import VideoFileIcon from '@mui/icons-material/VideoFile'
import LocationSearchingIcon from '@mui/icons-material/LocationSearching'
import PublicIcon from '@mui/icons-material/Public'
import AudioFileIcon from '@mui/icons-material/AudioFile'
import ImageIcon from '@mui/icons-material/Image'

import GenericTable from '../request-table/generic'
import Button from '../nura/button/button'
import SearchInput from '../nura/input/search'

import { useSnackbar } from '../snackbar/snackbar'
import styles from './media.module.css'
import SelectButton from '../nura/select-button'
import ImageViewer from '../dialogs/image-viewer'
import MediaUpload from '../dialogs/media-upload'

const ActiveItem = ({ fileType = "", onClose, ...rest }) => {
    if (fileType.includes("video/") || fileType.includes("audio/")) {
        return <VideoDialog
            title={rest?.fileName}
            src={rest?.bucketPath}
            isOpen
            onClose={onClose}
        />
    }

    if (fileType.includes("image/")) {
        return <ImageViewer
            title={rest?.fileName}
            src={rest?.bucketPath}
            show
            onClose={onClose}
        />
    }

    return null
}

const TypeToIcon = ({ type }) => {
    if (type.includes("video/")) {
        return <VideoFileIcon style={{ fontSize: 18 }} />
    }

    if (type.includes("audio/")) {
        return <AudioFileIcon style={{ fontSize: 18 }} />
    }

    if (type.includes("image/")) {
        return <ImageIcon style={{ fontSize: 18 }} />
    }
    
    return <InsertDriveFileIcon style={{ fontSize: 18 }} />
}

const StorageMetric = ({
    icon = () => null,
    title,
    itemCount,
    currentSize,
    totalSize,
    loading = false,
}) => {
    return (
        <div className={styles.storageMetric}>
            <div className={styles.storageContainer}>
                <div className={styles.iconContainer}>
                    {loading ? <CircularProgress size={15} /> : icon()}
                </div>

                <div className={styles.metricHeader}>
                    <div className={styles.metricTitle}>{title}</div>
                    {
                        loading ? (
                            <div className={styles.metricItems}>Unknown Items</div>
                        ) : (
                            <div className={styles.metricItems}>{itemCount} Items</div>
                        )
                    }
                </div>
            </div>

            <LinearProgress style={{
                height: 7,
                borderRadius: 10,
            }} value={(currentSize / totalSize) * 100} variant={loading ? "indeterminate" : "determinate"} />

            <div className={styles.mediaSizeInfo}>
                {
                    !loading && (
                        <>{formatBytes(currentSize)} of {formatBytes(totalSize)}</>
                    )
                }
            </div>
        </div>
    )
}

const newMediaOptions = () => [
    {name: "Upload", id: "upload"}
]

export default function Media() {
    const { activeProject, plan } = useSelector()
    const { enqueueSnackbar } = useSnackbar()
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [searchFilter, setSearchFilter] = useState("")
    const [activeNew, setActiveNew] = useState()

    const [activeItem, setActiveItem] = useState()
    const [deleteMediaShow, setDeleteMediaShow] = useState(false)

    const [checkedItems, setCheckedItems] = useState([])

    const media = useFetch(() => listMediaByBlueprintID(activeProject?.id), [])

    const tableContent = useMemo(() => media?.data?.media?.map(x => ({
        _id: x?.id,
        name: x?.fileName,
        type: x?.fileType,
        createdAt: formatDateTime(x?.createdAt),
        size: formatBytes(x?.fileSize),
        _icon: () => <TypeToIcon type={x?.fileType} />,
        _original: x,
    })).filter(x => x?.name.includes(searchFilter) || x?.type.includes(searchFilter)), [media?.data, searchFilter])

    const onClick = (data) => setActiveItem(data?._original)

    const deleteSelectedItems = async () => {
        setDeleteLoading(true)
        setDeleteMediaShow(false)
        try {
            await Promise.all(checkedItems.map(async (item) => await deleteMediaById(item)))
        } catch {
            enqueueSnackbar("Something went wrong deleting 1 or more items", { variant: "error" })
        }
        media.refresh()
        setDeleteLoading(false)
    }

    const handleNewSelect = (selection) => {
        switch(selection?.id) {
            case "upload": {
                setActiveNew(selection?.id)
                break
            }
        }
    }

    const closeMediaUpload = () => {
        setActiveNew()
        media.refresh()
    }

    useEffect(() => {
        setCheckedItems([])
    }, [searchFilter])

    return (
        <>
            {!!activeItem &&
                <ActiveItem {...activeItem} onClose={() => setActiveItem(undefined)} />
            }            
            <MediaUpload
                onClose={closeMediaUpload}
                show={activeNew === 'upload'}
            />
            
            <DeleteMediaDialog
                show={deleteMediaShow}
                setShow={setDeleteMediaShow}
                handleDelete={deleteSelectedItems}
            />
            <div className={styles.storageInfo}>
                <StorageMetric
                    title="Account Storage"
                    itemCount={media?.data?.media?.length}
                    currentSize={media?.data?.accountUsedSize}
                    totalSize={plan?.storageQuota*1024*1024}
                    icon={() => <PublicIcon />}
                    loading={media?.data?.loading}
                />
                <StorageMetric
                    title="Project Storage"
                    itemCount={media?.data?.media?.length}
                    currentSize={media?.data?.blueprintUsedSize}
                    totalSize={plan?.storageQuota*1024*1024}
                    icon={() => <LocationSearchingIcon />}
                    loading={media?.data?.loading}
                />
            </div>
            <DashboardHeader
                title="Media"
                sub={`(${checkedItems.length}/${tableContent?.length || 0})`}
                renderMore={() => (
                    <>
                        <SearchInput value={searchFilter} onChange={setSearchFilter} />
                    </>
                )}
            >
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                    <SelectButton title="New" options={newMediaOptions()} onSelect={handleNewSelect} />
                    <Button size="normal" variant="error" disabled={checkedItems.length === 0} onClick={() => setDeleteMediaShow(true)}>
                        Delete
                    </Button>
                    <IconButton style={{ color: 'var(--text-primary)', width: 45 }} onClick={media.refresh}>
                        <RefreshIcon style={{ fontSize: 20 }} />
                    </IconButton>
                </div>
            </DashboardHeader>
            <DashboardBody loading={media?.loading || deleteLoading} variant="nopad">
                {/* Enable horizontal scrolling for the table on narrow viewports */}
                <div className={styles.tableWrapper}>
                    <GenericTable
                        onClick={onClick}
                        data={tableContent}
                        setChecked={setCheckedItems}
                    />
                </div>
            </DashboardBody>
        </>
    )
}
